// ─── InlineAlert ────────────────────────────────────────────────────────────
// A message box that sits inside a form or panel.
//
//   <InlineAlert severity="error">That phone number and password don't match.</InlineAlert>
//   <InlineAlert severity="info">Please sign in to continue.</InlineAlert>
//   <InlineAlert severity="success">Password updated.</InlineAlert>
//   <InlineAlert severity="warning">Tiles: 27 marked, box fits 25.</InlineAlert>
//   <InlineAlert severity="error" onClose={() => setError('')}>…</InlineAlert>   adds a ✕ button
//
// The four kinds:
//   error   — something went wrong (red)
//   warning — worth a look, but not blocking (amber)
//   info    — neutral information, not anyone's fault (grey)
//   success — it worked (green)

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  ERROR, ERROR_BG, ERROR_BORDER,
  WARNING, WARNING_BG, WARNING_BORDER,
  SUCCESS, SUCCESS_BG, SUCCESS_BORDER,
  INFO, INFO_BG, INFO_BORDER,
  bodyFont,
} from '../../../theme/tokens2';

const STYLES = {
  error:   { color: ERROR,   bg: ERROR_BG,   border: ERROR_BORDER },
  warning: { color: WARNING, bg: WARNING_BG, border: WARNING_BORDER },
  success: { color: SUCCESS, bg: SUCCESS_BG, border: SUCCESS_BORDER },
  info:    { color: INFO,    bg: INFO_BG,    border: INFO_BORDER },
};

export default function InlineAlert({ severity = 'error', children, onClose, sx = {} }) {
  const s = STYLES[severity] || STYLES.error;
  // Screen readers announce "alert" boxes straight away; the others politely.
  const role = severity === 'error' ? 'alert' : 'status';

  return (
    <Box
      role={role}
      sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
        px: 2, py: 1.5,
        background: s.bg,
        border: `1px solid ${s.border}`,
        ...sx,
      }}
    >
      <Typography component="div" sx={{ ...bodyFont, fontSize: '13px', lineHeight: 1.55, color: s.color }}>
        {children}
      </Typography>

      {onClose && (
        <Box
          component="button"
          type="button"
          onClick={onClose}
          aria-label="Dismiss message"
          sx={{
            flexShrink: 0, width: 24, height: 24, mt: '-2px', mr: '-6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer', color: s.color,
            '&:focus-visible': { outline: `2px solid ${s.color}` },
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </Box>
      )}
    </Box>
  );
}
